import * as React from "react";
import { Input } from "./ui/input";

/**
 * Champ de saisie d'heure forcé en format 24h (HH:MM).
 *
 * Pourquoi pas <input type="time"> ?
 *   Chromium affiche le format selon la locale système du navigateur
 *   (en-US → "09:00 AM"), même avec `lang="fr-FR"` ou
 *   `Accept-Language: fr-FR`. Pour garantir un affichage strictement 24h
 *   indépendamment du navigateur/OS de l'utilisateur, on utilise un
 *   champ texte avec masque HH:MM.
 *
 *  Props :
 *    value      : string "HH:MM"
 *    onChange   : (v: string) => void  (renvoie toujours une valeur HH:MM
 *                 valide une fois la saisie complète, sinon la chaîne brute)
 *    placeholder: string (défaut "HH:MM")
 *    Tous les autres props (className, data-testid, disabled…) sont relayés.
 */
const TimeInput24 = React.forwardRef(({ value, onChange, placeholder = "HH:MM", ...props }, ref) => {
  const formatInput = (raw) => {
    // Conserver uniquement les chiffres, max 4
    const digits = (raw || "").replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const clamp = (v) => {
    // Si la valeur est complète HH:MM, on borne 0-23 / 0-59.
    const m = /^(\d{1,2}):?(\d{0,2})$/.exec(v || "");
    if (!m) return v;
    let h = m[1] === "" ? "" : Math.min(23, parseInt(m[1], 10));
    let mn = m[2] === "" ? "" : Math.min(59, parseInt(m[2], 10));
    if (h === "" && mn === "") return "";
    if (mn === "") return String(h).padStart(2, "0");
    return `${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
  };

  const handleChange = (e) => {
    const next = formatInput(e.target.value);
    onChange && onChange(next);
  };

  const handleBlur = (e) => {
    const next = clamp(e.target.value);
    if (next !== e.target.value) onChange && onChange(next);
    props.onBlur && props.onBlur(e);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      pattern="[0-9]{2}:[0-9]{2}"
      maxLength={5}
      value={value || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      {...props}
    />
  );
});

TimeInput24.displayName = "TimeInput24";

export default TimeInput24;
